import {
  calcOrderShippingFromLines,
  type ShippingLineInput,
} from '@/utils/mallSell'

export {
  calcLineShippingFee,
  calcOrderShippingFromLines,
  formatShippingChargeLabel,
} from '@/utils/mallSell'
export type { ShippingLineInput } from '@/utils/mallSell'

/**
 * 주문당(ONCE) 상품만 있을 때 — 담은 상품 배송비 중 최댓값.
 * 수량 연동 배송이 있으면 calcOrderShippingFromLines 를 쓴다.
 */
export function calcOrderShippingFee(fees: Array<number | undefined | null>): number {
  if (!fees.length) return 0
  return calcOrderShippingFromLines(
    fees.map((fee) => ({ qty: 1, shippingFee: fee, shippingCharge: 'ONCE' })),
  )
}

export function toShippingLine(
  product: {
    id?: string
    shippingFee?: number | null
    shippingCharge?: string | null
    shippingEvery?: number | null
  },
  qty: number,
): ShippingLineInput {
  return {
    productId: product.id,
    qty,
    shippingFee: product.shippingFee,
    shippingCharge: product.shippingCharge,
    shippingEvery: product.shippingEvery,
  }
}

/** 판매업체 출고 마감(예: 14:00)을 화면용으로 짧게 쓴다. */
export function formatCutoffLabel(cutoff: string | undefined | null): string {
  const raw = String(cutoff || '').trim()
  if (!raw) return ''
  const matched = raw.match(/^(\d{1,2}):00$/)
  if (matched) return `${Number(matched[1])}시`
  return raw
}

/**
 * 화면 멘트 `mall.product.same_day_cutoff` 본문.
 * `{cutoff}` 자리에 마감 시각(예: 14시)을 넣는다. 본문이 비면 안내를 숨긴다.
 */
export function formatSameDayCutoffText(
  template: string | undefined | null,
  cutoff: string | undefined | null,
): string {
  const raw = String(template ?? '').trim()
  if (!raw) return ''
  const label = formatCutoffLabel(cutoff)
  if (raw.includes('{cutoff}') && !label) return ''
  return raw.split('{cutoff}').join(label)
}

/** 상품 배송비 한 건 — 0원이면 「무료」. */
export function formatShippingFeeAmount(fee: number | undefined | null): string {
  const value = Math.max(0, Number(fee) || 0)
  if (value === 0) return '무료'
  return `${value.toLocaleString('ko-KR')}원`
}

/**
 * 스토어·목록용 배송비 요약.
 * 고객에게는 스토어 기본값이 아니라 실제 상품 배송비를 보여 준다.
 */
export function summarizeProductShippingFees(
  fees: Array<number | undefined | null>,
): string {
  const values = fees.map((fee) => Math.max(0, Number(fee) || 0))
  if (!values.length) return ''
  const min = Math.min(...values)
  const max = Math.max(...values)
  if (min === max) {
    return min === 0 ? '배송비 무료' : `배송비 ${min.toLocaleString('ko-KR')}원`
  }
  return `배송비 ${formatShippingFeeAmount(min)} ~ ${max.toLocaleString('ko-KR')}원`
}
